import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../core/winpresentation/SimplePresentationRunner";
import {BS2} from "../../BS2";
import {gsap} from "gsap";
import {replaceWithNewSymbolDataType, XSplitData, XSplitDirection} from "../../server/BS2Data";
import {BS2Statics} from "../../config/BS2Statics";
import {StateMatrixCoordinate} from "../../utils/StateMatrix";
import {BS2Symbol} from "../../reels/BS2Symbol";
import {XSplitSymbol} from "../../reels/XSplitSymbol";
import {WildSymbol} from "../../reels/WildSymbol";
import {SimpleReelModelSymbolData} from "../../../core/reel/simplereel/SimpleReelModel";
import {BS2Sound} from "../../config/sound/BS2Sound";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {SanityCheckPresentation} from "../maingame/win/SanityCheckPresentation";

export class XSplitPresentation implements SimplePresentation {
    private _mainTL: gsap.core.Timeline | undefined;
    private _isAborted: boolean;

    constructor() {
    }

    shouldRun(): boolean {
        return !!(BS2.model.xSplitData && BS2.model.xSplitData.length > 0);
    }

    start(controller: SimplePresentationRunnerController): void {
        this._isAborted = false;
        const tl: gsap.core.Timeline = gsap.timeline({
                onComplete: (): void => {
                    controller.setSkipCallback();
                    controller.finish();
                }
            }),
            xSplitData: XSplitData[][] | undefined = BS2.model.xSplitData,
            isWin: boolean = BS2.model.winData.winRatio > 0;
        // tl.to(this, {duration: isWin ? 0.6 : 3});
        tl.add(() => controller.setSkipCallback(() => {
            BS2.soundPlayer.stopSound(BS2Sound.G_BUILDUP);
            BS2.soundPlayer.stopSound(BS2Sound.D_BUILDUP);
            this._isAborted = true;
            this._mainTL?.timeScale(3);
        }));
        if (xSplitData) {
            this.assignValueToNumbOfSplitToSym(xSplitData); // used to assign values to _numberOfSplits in BS2Symbols.
            tl.add(this.updateAllSplitSymSelfSize(xSplitData));
        }
        const soundFlags: Record<number, {
            firstDaggerForSplit: boolean,
            firstDaggerForWild: boolean,
            firstGuillotineForBuildup: boolean,
            firstGuillotineForSplit: boolean,
            firstGuillotineForWild: boolean
        }> = {};
        if (xSplitData) {
            for (let symIdx: number = 0; symIdx < xSplitData.length; symIdx++) {
                if (xSplitData[symIdx].length > 0) {
                    const daggerSplitTL: gsap.core.Timeline = gsap.timeline(),
                        guillotineSplitTL: gsap.core.Timeline = gsap.timeline();

                    for (let splitIdx: number = 0; splitIdx < xSplitData[symIdx].length; splitIdx++) {
                        const sym: XSplitSymbol = <XSplitSymbol>(BS2.reelView.reels[xSplitData[symIdx][splitIdx].splitSymCoordinate.column].model.getVisibleSymbolAt(xSplitData[symIdx][splitIdx].splitSymCoordinate.row)).positionable;
                        const reel: number = xSplitData[symIdx][splitIdx].splitSymCoordinate.column;
                        const row: number = xSplitData[symIdx][splitIdx].splitSymCoordinate.row;
                        const individualDaggerTl: gsap.core.Timeline = gsap.timeline();
                        const individualGuillotineTl: gsap.core.Timeline = gsap.timeline();

                        if (!soundFlags[row]) {
                            soundFlags[row] = {
                                firstDaggerForSplit: true,
                                firstDaggerForWild: true,
                                firstGuillotineForBuildup: true,
                                firstGuillotineForSplit: true,
                                firstGuillotineForWild: true,
                            };
                        }

                        if (xSplitData[symIdx][splitIdx].direction === XSplitDirection.HORIZONTAL) {
                            individualDaggerTl.add([sym.getSplitAnimation(xSplitData[symIdx][splitIdx]),
                                (): void => {
                                    if (!this._isAborted) {
                                        BS2.soundPlayer.playSound(BS2Sound.D_BUILDUP);
                                    }
                                }
                            ]);

                            individualDaggerTl.add(() => {
                                if (!this._isAborted) {
                                    BS2.soundPlayer.playSound(BS2Sound.D_SPLIT);
                                } else if (this._isAborted && soundFlags[row].firstDaggerForSplit) {
                                    BS2.soundPlayer.playSound(BS2Sound.D_SPLIT);
                                    soundFlags[row].firstDaggerForSplit = false;
                                }
                            }, "<+0.7");
                            individualDaggerTl.add(() => {
                                if (!this._isAborted) {
                                    BS2.soundPlayer.playSound(BS2Sound.X_SPLIT_TO_WILD);
                                } else if (this._isAborted && soundFlags[row].firstDaggerForWild) {
                                    BS2.soundPlayer.playSound(BS2Sound.X_SPLIT_TO_WILD);
                                    soundFlags[row].firstDaggerForWild = false;
                                }
                            }, "<+0.5");

                            individualDaggerTl.add(this.getSymbolSplitAnimations({
                                column: reel,
                                row: row
                            }, XSplitDirection.HORIZONTAL), "0.5");
                            daggerSplitTL.add(individualDaggerTl, "<+=0.2");
                        } else {
                            individualGuillotineTl.add([sym.getSplitAnimation(xSplitData[symIdx][splitIdx]),
                                (): void => {
                                    if (!this._isAborted && soundFlags[row].firstGuillotineForBuildup) {
                                        BS2.soundPlayer.playSound(BS2Sound.G_BUILDUP);
                                        soundFlags[row].firstGuillotineForBuildup = false;
                                    }
                                }
                            ], 0);

                            individualGuillotineTl.add(() => {
                                if (soundFlags[row].firstGuillotineForSplit) {
                                    BS2.soundPlayer.playSound(BS2Sound.G_SPLIT);
                                    soundFlags[row].firstGuillotineForSplit = false;
                                }
                            }, "<+0.6");
                            individualGuillotineTl.add(() => {
                                if (soundFlags[row].firstGuillotineForWild) {
                                    BS2.soundPlayer.playSound(BS2Sound.X_SPLIT_TO_WILD);
                                    soundFlags[row].firstGuillotineForWild = false;
                                }
                            }, "<+0.7");

                            individualGuillotineTl.add(this.getSymbolSplitAnimations({
                                column: reel,
                                row: row
                            }, XSplitDirection.VERTICAL), "0.7");
                            guillotineSplitTL.add(individualGuillotineTl, 0);
                        }
                    }
                    tl.add(daggerSplitTL);
                    tl.add(guillotineSplitTL);
                }
            }
        }

        tl.add((): void => {
            if (xSplitData) {
                xSplitData.forEach((row: XSplitData[]): void => {
                    row.forEach((ele: XSplitData): void => {
                        const replacedSymData: replaceWithNewSymbolDataType = BS2.reelView.reels[ele.splitSymCoordinate.column].model.replaceWithNewSymbol(ele.splitSymCoordinate.row + 1, BS2Statics.SYMBOLS.W),
                            currMulti: number = (<BS2Symbol>replacedSymData.oldSymbol.positionable).currMultiValue;
                        (<WildSymbol>replacedSymData.newSymbol.positionable).setMultiplier(currMulti, true);
                        (<BS2Symbol>replacedSymData.oldSymbol.positionable).destroy();
                    });
                });
            }
        });
        if (!isWin || BS2.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
            tl.timeScale(4);
        }
        if (this._mainTL) {
            this._mainTL.progress(1);
            this._mainTL = undefined;
        }
        this._mainTL = tl;
    }


    private assignValueToNumbOfSplitToSym(xSplitData: XSplitData[][]): void {
        for (let symIdx: number = 0; symIdx < xSplitData.length; symIdx++) {
            for (let j: number = 0; j < xSplitData[symIdx].length; j++) {
                if (xSplitData[symIdx][j].direction === XSplitDirection.VERTICAL) {
                    for (let k: number = xSplitData[symIdx][j].splitSymCoordinate.row; k <= BS2Statics.BOT_STICKY_WILD_IDX_VIS_AREA; k++) {
                        if (k === xSplitData[symIdx][j].splitSymCoordinate.row) {
                            continue; //xSplits does not split isSelf in code
                        }
                        const numberOfDaggerSplits: number = (<BS2Symbol>BS2.reelView.reels[xSplitData[symIdx][j].splitSymCoordinate.column].model.getVisibleSymbolAt(k).positionable).numberOfSplitsOnIt;
                        (<BS2Symbol>BS2.reelView.reels[xSplitData[symIdx][j].splitSymCoordinate.column].model.getVisibleSymbolAt(k).positionable).numberOfSplitsOnIt = numberOfDaggerSplits + 1;
                    }
                }
                if (xSplitData[symIdx][j].direction === XSplitDirection.HORIZONTAL) {
                    for (let reelIdx: number = 0; reelIdx < BS2Statics.NR_OF_REELS; reelIdx++) {
                        if (reelIdx === xSplitData[symIdx][j].splitSymCoordinate.column) {
                            continue; //xSplits does not split isSelf in code
                        }
                        const numberOfDaggerSplits: number = (<BS2Symbol>BS2.reelView.reels[reelIdx].model.getVisibleSymbolAt(symIdx).positionable).numberOfSplitsOnIt;
                        (<BS2Symbol>BS2.reelView.reels[reelIdx].model.getVisibleSymbolAt(symIdx).positionable).numberOfSplitsOnIt = numberOfDaggerSplits + 1;
                    }
                }

                //each split sym increases it's size to 2
                const numberOfDaggerSplits: number = (<BS2Symbol>BS2.reelView.reels[xSplitData[symIdx][j].splitSymCoordinate.column].model.getVisibleSymbolAt(xSplitData[symIdx][j].splitSymCoordinate.row).positionable).numberOfSplitsOnIt;
                (<BS2Symbol>BS2.reelView.reels[xSplitData[symIdx][j].splitSymCoordinate.column].model.getVisibleSymbolAt(xSplitData[symIdx][j].splitSymCoordinate.row).positionable).numberOfSplitsOnIt = numberOfDaggerSplits + 1;
            }
        }
    }

    private updateAllSplitSymSelfSize(xSplitData: XSplitData[][]): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            BS2.soundPlayer.playSound(BS2Sound.X_SPLIT_X2)
        }, 0);
        xSplitData.forEach((row: XSplitData[]): void => {
            row.forEach((ele: XSplitData): void => {
                const sym: BS2Symbol = <BS2Symbol>BS2.reelView.reels[ele.splitSymCoordinate.column].model.getVisibleSymbolAt(ele.splitSymCoordinate.row).positionable;
                tl.add(sym.getMultiplierUpdateAnimation(2, undefined), 0);
                sym.currMultiValue = 2;
            })
        })
        return tl;
    }

    private getOffSetTiming(offset: number, direction: XSplitDirection): number {
        let timing: number = 0;
        switch (offset) {
            case 0:
                return 0;
            case 1:
                return 0.2;
            case 2:
                return direction === XSplitDirection.VERTICAL ? 0.23 : 0.3;
            case 3:
                return direction === XSplitDirection.VERTICAL ? 0.27 : 0.33;
            case 4:
                return direction === XSplitDirection.VERTICAL ? 0.3 : 0.34;
        }
        return timing;
    }

    private getSymbolSplitAnimations(scatterPos: StateMatrixCoordinate, direction: XSplitDirection): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            splitSymCoord: StateMatrixCoordinate[] = [];

        if (direction === XSplitDirection.VERTICAL) {
            for (let symIdx: number = scatterPos.row + 1; symIdx < 5; ++symIdx) {
                if (scatterPos.row === symIdx) {
                    continue; //xSplits does not split isSelf in code
                }
                if ((scatterPos.row + 1 >= 5)) {
                    break;
                }
                splitSymCoord.push({column: scatterPos.column, row: symIdx});
            }
        } else {
            for (let reelIdx: number = 0; reelIdx < BS2Statics.NR_OF_REELS; ++reelIdx) {
                if (scatterPos.column === reelIdx) {
                    continue; //xSplits does not split isSelf in code
                }
                splitSymCoord.push({column: reelIdx, row: scatterPos.row});
            }
        }

        for (let coord of splitSymCoord) {
            const sym: BS2Symbol | XSplitSymbol = <BS2Symbol>BS2.reelView.reels[coord.column].model.getVisibleSymbolAt(coord.row).positionable,
                offset: number = Math.abs(scatterPos.column - coord.column) + Math.abs(scatterPos.row - coord.row);
            let splitValue: number = direction === XSplitDirection.VERTICAL ? (sym.currMultiValue * BS2.model.gameData.symbolSizes[scatterPos.column][scatterPos.row]) : (sym.currMultiValue * 2);
            if (splitValue > 32) {
                splitValue = 32;
            }
            if (BS2.model.evaluatedArea.matrix[coord.column][coord.row] === BS2Statics.SYMBOLS.W) {
                if (BS2.reelView.bottomStickyWildView.isVisible(coord.column) && !BS2.reelView.bottomStickyWildView.isLocked(coord.column) && coord.row === BS2Statics.BOT_STICKY_WILD_IDX_VIS_AREA) {
                    tl.add(sym.getMultiplierUpdateAnimation(splitValue, this.getSplitLineDirection(coord, direction)), this.getOffSetTiming(offset, direction));
                    sym.currMultiValue = splitValue;
                } else if (sym.name !== BS2Statics.SYMBOLS.Y) {
                    tl.add(sym.getMultiplierUpdateAnimation(splitValue, this.getSplitLineDirection(coord, direction)), this.getOffSetTiming(offset, direction));
                    sym.currMultiValue = splitValue;
                }
            } else if (sym.name !== BS2Statics.SYMBOLS.Y) {
                tl.add(sym.getMultiplierUpdateAnimation(splitValue, this.getSplitLineDirection(coord, direction)), this.getOffSetTiming(offset, direction));
                sym.currMultiValue = splitValue;
            }
        }
        return tl;
    }

    private getSplitLineDirection(symPos: StateMatrixCoordinate, splitDirection: XSplitDirection): "vertical" | "horizontal" | "cross" {
        let direction: "vertical" | "horizontal" | "cross" = "vertical";
        const symbols: SimpleReelModelSymbolData [] = BS2.reelView.reels[symPos.column].model.getAllVisibleSymbols();
        if (splitDirection === XSplitDirection.HORIZONTAL) {
            for (let i: number = 0; i < symPos.row; i++) {
                if (symbols[i].name === BS2Statics.SYMBOLS.G) {
                    return "cross";
                }
            }
            if ((<BS2Symbol>symbols[symPos.row].positionable).isVerticallySplit) {
                direction = "cross";
            } else {
                direction = "horizontal";
            }
        } else {
            if ((<BS2Symbol>symbols[symPos.row].positionable).isHorizontallySplit) {
                direction = "cross";
            } else {
                direction = "vertical";
            }
        }
        return direction;
    }

    private isLastDaggerToSplit(scatterPos: StateMatrixCoordinate): boolean {
        const rowSplitData = BS2.model.xSplitData && BS2.model.xSplitData[scatterPos.row];
        let lastDaggerFlag: number = 0;
        rowSplitData?.forEach((ele) => {
            if (ele.splitSymCoordinate.column > lastDaggerFlag) {
                lastDaggerFlag = ele.splitSymCoordinate.column;
            }
        })
        return lastDaggerFlag === scatterPos.column;
    }
}